import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { CURRENCY_CODE, CurrencyType } from '../../enum/constants';
import { BoardColumnColor, BoardColumnType } from '../../components/board-v3/board-v3.types';

const DEFAULT_COLUMN_TYPE: BoardColumnType = 'DEFAULT';
const COLUMN_TYPE_COLORS: Record<BoardColumnType, BoardColumnColor> = {
  DEFAULT: { background: '#f6f8f9', text: '#000000' },
  SUCCESS: { background: '#27ae60', text: '#1a1a1a' },
  FAILED: { background: '#e74c3c', text: '#ffffff' },
};

interface BoardColumnEditForm {
  name: FormControl<string | null>;
  type: FormControl<BoardColumnType | null>;
  bg_color: FormControl<string | null>;
  text_color: FormControl<string | null>;
}

interface BoardColumnEditFormValue {
  name: string | null;
  type: BoardColumnType | null;
  bg_color: string | null;
  text_color: string | null;
}

interface BoardColumnEditColumn {
  name?: string | null;
  type?: BoardColumnType | null;
  color?: Partial<BoardColumnColor> | null;
}

interface BoardColumnEditData {
  column?: BoardColumnEditColumn;
  funnelCurrency?: CurrencyType | string;
  canEditType?: boolean;
  excludedColumnTypes?: BoardColumnType[];
}

@Component({
  selector: 'app-board-column-edit',
  templateUrl: './board-column-edit.component.html',
  styleUrls: ['./board-column-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardColumnEditComponent implements OnInit, OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<BoardColumnEditComponent>);
  private readonly data = inject<{ data: BoardColumnEditData }>(MAT_DIALOG_DATA);
  private readonly destroy$ = new Subject<null>();
  private initialValue!: BoardColumnEditFormValue;

  public editColumnForm!: FormGroup<BoardColumnEditForm>;
  public hasChanges = false;
  public currencies = CURRENCY_CODE;
  public canEditType = false;
  public columnTypeOptions: { name: string; value: BoardColumnType }[] = [
    { name: 'board_v3_column_type_default', value: 'DEFAULT' },
    { name: 'board_v3_column_type_success', value: 'SUCCESS' },
    { name: 'board_v3_column_type_failed', value: 'FAILED' },
  ];

  ngOnInit(): void {
    const column = this.data?.data?.column ?? {};
    this.canEditType = this.data?.data?.canEditType ?? false;
    const excludedColumnTypes = new Set(this.data?.data?.excludedColumnTypes ?? []);
    this.columnTypeOptions = this.columnTypeOptions.filter((option) => !excludedColumnTypes.has(option.value));

    this.editColumnForm = new FormGroup<BoardColumnEditForm>({
      name: new FormControl(column.name || '', [Validators.required, Validators.maxLength(255)]),
      type: new FormControl<BoardColumnType | null>(column.type || DEFAULT_COLUMN_TYPE, Validators.required),
      bg_color: new FormControl(column.color?.background || '#f6f8f9', Validators.required),
      text_color: new FormControl(column.color?.text || '#000000', Validators.required),
    });

    this.initialValue = this.editColumnForm.getRawValue();
    this.editColumnForm.controls.type.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((type) => {
      this.applySystemColor(type);
    });
    this.editColumnForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.hasChanges = this.hasFormChanges(value);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  public onSave(): void {
    this.editColumnForm.markAllAsTouched();
    if (this.editColumnForm.invalid) {
      return;
    }

    this.dialogRef.close({
      updatedColumn: {
        ...this.editColumnForm.getRawValue(),
      },
    });
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  private hasFormChanges(value: Partial<BoardColumnEditFormValue>): boolean {
    const currentValue = { ...this.editColumnForm.getRawValue(), ...value };
    return (Object.keys(this.initialValue) as (keyof BoardColumnEditFormValue)[]).some(
      (key) => this.initialValue[key] !== currentValue[key],
    );
  }

  private applySystemColor(type: BoardColumnType | null): void {
    if (!this.canEditType) return;

    const color = type ? COLUMN_TYPE_COLORS[type] : null;
    if (!color) return;

    this.editColumnForm.patchValue(
      {
        bg_color: color.background,
        text_color: color.text,
      },
      { emitEvent: false },
    );
    this.hasChanges = this.hasFormChanges(this.editColumnForm.getRawValue());
  }
}
